import { useMemo } from "react";
import { useLocation } from "react-router";
import { trpc } from "@/lib/trpc";

export type AtendimentoEmCurso = {
  id: string;
  dentistId: string | null;
  patientName: string;
  startedAt: Date | string | null;
};

/**
 * Quem está em atendimento agora, indexado por dentista — a mesma leitura na lista de
 * profissionais da Agenda e nos cartões da Equipe.
 *
 * O escopo é resolvido no servidor (`appointment.emAtendimento`): quem não tem `agenda.viewAll`
 * recebe só o próprio atendimento, então o selo nunca revela o que a pessoa não poderia ver.
 */
export function useEmAtendimento(habilitado = true) {
  const query = trpc.appointment.emAtendimento.useQuery(undefined, {
    enabled: habilitado,
    // O selo é informação de "agora": vale reconsultar de vez em quando sem depender de o
    // usuário mexer em algo que invalide a agenda.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const porDentista = useMemo(() => {
    const mapa = new Map<string, AtendimentoEmCurso>();
    for (const a of (query.data ?? []) as AtendimentoEmCurso[]) {
      if (a.dentistId && !mapa.has(a.dentistId)) mapa.set(a.dentistId, a);
    }
    return mapa;
  }, [query.data]);

  return { porDentista, carregando: query.isLoading };
}

/**
 * O atendimento que o atalho do topo deve oferecer — ou `null` quando não há o que oferecer.
 *
 * Fica aqui, e não dentro do botão, porque o cabeçalho precisa saber ANTES de montar o layout: com
 * atalho em cena ele reserva uma faixa central; sem atalho, o título fica com a largura toda.
 */
export type MeuAtendimento = {
  id: string;
  patientId: string | null;
  patientName: string;
  startedAt: Date | string | null;
  organizationId: string;
  organizationName: string;
};

/** É o MEU atendimento (sou o profissional), em qualquer organização — o atendimento é de quem está
 *  na cadeira, não da organização ativa. `atual` é o mais recente; `outros`, os demais abertos. */
export function useAtalhoDeAtendimento(): { atual: MeuAtendimento; outros: MeuAtendimento[] } | null {
  const query = trpc.appointment.meuAtendimentoEmCurso.useQuery(undefined, {
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const location = useLocation();
  // Já estou na ficha de um deles — o atalho oferece os outros (ou some).
  const naFicha = new URLSearchParams(location.search).get("atendimento");
  const lista = ((query.data ?? []) as MeuAtendimento[]).filter((a) => a.id !== naFicha);
  const [atual, ...outros] = lista;
  return atual ? { atual, outros } : null;
}

/** "há 12 min" a partir do início real do atendimento. */
export function haQuantoTempo(startedAt: Date | string | null | undefined): string | null {
  if (!startedAt) return null;
  const minutos = Math.max(0, Math.round((Date.now() - new Date(startedAt).getTime()) / 60000));
  if (minutos < 1) return "agora";
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  return `há ${horas}h${minutos % 60 ? ` ${minutos % 60}min` : ""}`;
}
